import { useEffect, useState } from 'react';
import {
    readStoredBrandsOnPage,
    readStoredBrandsPage,
    readStoredProductsOnPage,
    readStoredProductsPage,
    writeStoredBrandsOnPage,
    writeStoredBrandsPage,
    writeStoredProductsOnPage,
    writeStoredProductsPage,
} from '@/context/reviews-page-transport.helpers';

type UseReviewsClientPagePaginationArgs = {
    clientId: string;
    productsLength: number;
    brandGroupsLength: number;
};

export const useReviewsClientPagePagination = ({
    clientId,
    productsLength,
    brandGroupsLength,
}: UseReviewsClientPagePaginationArgs) => {
    const [productsPage, setProductsPage] = useState(1);
    const [productsOnPage, setProductsOnPage] = useState(10);
    const [productsPageCount, setProductsPageCount] = useState(1);
    const [productsTotalRows, setProductsTotalRows] = useState(productsLength);
    const [brandsPage, setBrandsPage] = useState(1);
    const [brandsOnPage, setBrandsOnPage] = useState(10);
    const [brandsPageCount, setBrandsPageCount] = useState(1);
    const [brandsTotalRows, setBrandsTotalRows] = useState(brandGroupsLength);
    const [isStorageHydrated, setIsStorageHydrated] = useState(false);

    useEffect(() => {
        setIsStorageHydrated(false);
        setProductsPage(readStoredProductsPage(clientId));
        setProductsOnPage(readStoredProductsOnPage(clientId));
        setBrandsPage(readStoredBrandsPage(clientId));
        setBrandsOnPage(readStoredBrandsOnPage(clientId));
        setIsStorageHydrated(true);
    }, [clientId]);

    useEffect(() => {
        setProductsTotalRows(productsLength);
    }, [productsLength]);

    useEffect(() => {
        setBrandsTotalRows(brandGroupsLength);
    }, [brandGroupsLength]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsPage(clientId, productsPage);
    }, [clientId, isStorageHydrated, productsPage]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredProductsOnPage(clientId, productsOnPage);
    }, [clientId, isStorageHydrated, productsOnPage]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredBrandsPage(clientId, brandsPage);
    }, [brandsPage, clientId, isStorageHydrated]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredBrandsOnPage(clientId, brandsOnPage);
    }, [brandsOnPage, clientId, isStorageHydrated]);

    const onProductsOnPageUpdate = (count: number) => {
        setProductsOnPage(writeStoredProductsOnPage(clientId, count));
        setProductsPage(1);
        writeStoredProductsPage(clientId, 1);
    };

    const onBrandsOnPageUpdate = (count: number) => {
        setBrandsOnPage(writeStoredBrandsOnPage(clientId, count));
        setBrandsPage(1);
        writeStoredBrandsPage(clientId, 1);
    };

    return {
        productsPage,
        setProductsPage,
        productsOnPage,
        setProductsOnPage,
        productsPageCount,
        setProductsPageCount,
        productsTotalRows,
        setProductsTotalRows,
        brandsPage,
        setBrandsPage,
        brandsOnPage,
        setBrandsOnPage,
        brandsPageCount,
        setBrandsPageCount,
        brandsTotalRows,
        setBrandsTotalRows,
        onProductsOnPageUpdate,
        onBrandsOnPageUpdate,
    };
};
